/**
 * Model Risk Matrix Component (DC-922)
 *
 * FDA AI/ML Guidance: Risk = f(Model Influence × Decision Consequence)
 * Visual 3x4 matrix for selecting risk parameters with auto-calculated risk level.
 *
 * Following sensitivity-selector pattern from ProblemDeclarationForm.tsx
 */

import React, { useMemo } from 'react';
import './contextual-validity.css';

// ============================================================================
// TYPES (mirror server types for client-side use)
// ============================================================================

export type ModelInfluence = 'low' | 'medium' | 'high';
export type DecisionConsequence = 'low' | 'medium' | 'high' | 'critical';
export type CalculatedRiskLevel = 'low' | 'medium' | 'high';

export interface ModelRiskMatrixProps {
  modelInfluence: ModelInfluence | undefined;
  decisionConsequence: DecisionConsequence | undefined;
  onInfluenceChange: (value: ModelInfluence) => void;
  onConsequenceChange: (value: DecisionConsequence) => void;
  disabled?: boolean;
}

// ============================================================================
// RISK CALCULATION (same logic as server/contextualValidity/types.ts)
// ============================================================================

const MODEL_RISK_MATRIX: Record<ModelInfluence, Record<DecisionConsequence, CalculatedRiskLevel>> = {
  low: {
    low: 'low',
    medium: 'low',
    high: 'medium',
    critical: 'medium',
  },
  medium: {
    low: 'low',
    medium: 'medium',
    high: 'medium',
    critical: 'high',
  },
  high: {
    low: 'medium',
    medium: 'medium',
    high: 'high',
    critical: 'high',
  },
};

export function calculateModelRisk(
  influence: ModelInfluence | undefined,
  consequence: DecisionConsequence | undefined
): CalculatedRiskLevel | undefined {
  if (!influence || !consequence) return undefined;
  return MODEL_RISK_MATRIX[influence][consequence];
}

// ============================================================================
// OPTION DEFINITIONS
// ============================================================================

const INFLUENCE_OPTIONS: Array<{
  value: ModelInfluence;
  label: string;
  description: string;
}> = [
  {
    value: 'low',
    label: 'Low',
    description: 'Model provides suggestions only; humans make all decisions',
  },
  {
    value: 'medium',
    label: 'Medium',
    description: 'Model recommendations influence but don\'t determine decisions',
  },
  {
    value: 'high',
    label: 'High',
    description: 'Model output directly drives decisions with limited human review',
  },
];

const CONSEQUENCE_OPTIONS: Array<{
  value: DecisionConsequence;
  label: string;
  description: string;
}> = [
  {
    value: 'low',
    label: 'Low',
    description: 'Wrong output has minimal impact; easily reversible',
  },
  {
    value: 'medium',
    label: 'Medium',
    description: 'Wrong output causes inconvenience or moderate harm',
  },
  {
    value: 'high',
    label: 'High',
    description: 'Wrong output causes significant harm or financial loss',
  },
  {
    value: 'critical',
    label: 'Critical',
    description: 'Wrong output may cause irreversible harm, injury, or death',
  },
];

const RISK_LEVEL_DESCRIPTIONS: Record<CalculatedRiskLevel, string> = {
  low: 'Standard validation sufficient. Follow general best practices.',
  medium: 'Enhanced validation required. Document assumptions and limitations.',
  high: 'Rigorous validation required. Independent review recommended. Consider FDA pre-submission.',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ModelRiskMatrix: React.FC<ModelRiskMatrixProps> = ({
  modelInfluence,
  decisionConsequence,
  onInfluenceChange,
  onConsequenceChange,
  disabled = false,
}) => {
  const calculatedRisk = useMemo(
    () => calculateModelRisk(modelInfluence, decisionConsequence),
    [modelInfluence, decisionConsequence]
  );

  return (
    <div className="model-risk-matrix">
      <div className="risk-matrix-header">
        <h4>Model Risk Assessment</h4>
        <p className="risk-matrix-subtitle">
          Per FDA AI/ML guidance: higher influence + higher consequence = higher validation rigor
        </p>
      </div>

      {/* Model Influence Selection */}
      <div className="form-group">
        <label>Model Influence</label>
        <p className="field-explanation">
          How much does the model output influence the final decision?
        </p>
        <div className="risk-selector influence-selector">
          {INFLUENCE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`risk-option ${modelInfluence === option.value ? 'selected' : ''} influence-${option.value}`}
              onClick={() => onInfluenceChange(option.value)}
              disabled={disabled}
              title={option.description}
            >
              <span className="risk-option-label">{option.label}</span>
            </button>
          ))}
        </div>
        {modelInfluence && (
          <span className="field-hint">
            {INFLUENCE_OPTIONS.find((o) => o.value === modelInfluence)?.description}
          </span>
        )}
      </div>

      {/* Decision Consequence Selection */}
      <div className="form-group">
        <label>Decision Consequence</label>
        <p className="field-explanation">
          What is the severity of consequences if the model produces wrong output?
        </p>
        <div className="risk-selector consequence-selector">
          {CONSEQUENCE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`risk-option ${decisionConsequence === option.value ? 'selected' : ''} consequence-${option.value}`}
              onClick={() => onConsequenceChange(option.value)}
              disabled={disabled}
              title={option.description}
            >
              <span className="risk-option-label">{option.label}</span>
            </button>
          ))}
        </div>
        {decisionConsequence && (
          <span className="field-hint">
            {CONSEQUENCE_OPTIONS.find((o) => o.value === decisionConsequence)?.description}
          </span>
        )}
      </div>

      {/* Calculated Risk Level Display */}
      {calculatedRisk && (
        <div className={`calculated-risk-display risk-level-${calculatedRisk}`}>
          <div className="risk-level-header">
            <span className="risk-level-label">Calculated Risk Level:</span>
            <span className={`risk-level-badge ${calculatedRisk}`}>
              {calculatedRisk.toUpperCase()}
            </span>
          </div>
          <p className="risk-level-description">
            {RISK_LEVEL_DESCRIPTIONS[calculatedRisk]}
          </p>
        </div>
      )}

      {/* Empty state when not both selected */}
      {(!modelInfluence || !decisionConsequence) && (
        <div className="calculated-risk-display risk-level-empty">
          <p className="risk-level-prompt">
            Select both Model Influence and Decision Consequence to calculate risk level
          </p>
        </div>
      )}
    </div>
  );
};

export default ModelRiskMatrix;
